import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useMemo } from 'react'

import {
  CollectionFeeDetailFragment,
  FeeDetailFragment,
  ItemType,
  Maybe,
  PendingPurchaseItemDetailFragment,
  PendingPurchaseItemStatus,
  PersonalInformationType,
  UserAddressFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import {
  OrderItemDetailSkeleton,
  PaymentInfo,
  PurchaseInfo,
  SettlementTag,
  ShippingAddress,
} from '@/components/organisms'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import {
  getActiveMembershipSubscriptionProductPlan,
  MembershipSubscriptionProduct,
} from '@/core/membership'

import {
  OrderItemCell,
  TotalPaymentCell,
  TotalPaymentCellProps,
} from '../../StoreItemPurchaseProceed/template'

export * from './fragments'

type PurchaseType = 'cash' | 'konbini' | 'prepaid_point'

export type State = {
  orderItems: Maybe<PendingPurchaseItemDetailFragment['items']>
  pendingPurchaseItem: PendingPurchaseItemDetailFragment
  address: Maybe<UserAddressFragment['address']>
  fullName: string
  phoneNumber: string
  loading: boolean
  isPhoneNumberVerified: boolean
  systemFee: Maybe<CollectionFeeDetailFragment>[]
  shippingFee: Maybe<FeeDetailFragment>
  konbiniFee: Maybe<FeeDetailFragment>
  artist: Maybe<PendingPurchaseItemDetailFragment['artist']>
  purchaseType?: PurchaseType
  paymentType?: UserPaymentType
  formattedPrepaidPointBarance: string | number
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const PaymentStatusDetailTemplate: Template<State, Action> = ({
  state,
}) => {
  const { t } = useTranslation('paymentStatusDetail')
  const { generatePath, routerPages, routerNames } = useRouter()

  const renderBottomItem = useCallback(() => {
    if (
      state.pendingPurchaseItem?.status !==
      PendingPurchaseItemStatus.WaitingPayment
    ) {
      return null
    }
    return (
      <FixedBottomCell
        expiredAt={state.pendingPurchaseItem?.formattedExpiredAt ?? ''}
        voucherUrl={state.pendingPurchaseItem?.voucherUrl ?? ''}
      />
    )
  }, [
    state.pendingPurchaseItem?.formattedExpiredAt,
    state.pendingPurchaseItem?.status,
    state.pendingPurchaseItem?.voucherUrl,
  ])

  // 個人情報の登録必須項目
  const personalInformationRequirements = useMemo<string[]>(() => {
    if (!state.pendingPurchaseItem) {
      return []
    }

    const result = state.pendingPurchaseItem.items?.reduce(
      (acc: string[], item) => {
        const requirements = item.item.personalInformationRequirements

        return Array.isArray(requirements) ? acc.concat(requirements) : acc
      },
      []
    )

    return [...new Set(result)]
  }, [state.pendingPurchaseItem])

  const product = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      state.pendingPurchaseItem?.artist,
      state.pendingPurchaseItem?.metadata?.membership?.planId ?? ''
    )
  }, [
    state.pendingPurchaseItem?.artist,
    state.pendingPurchaseItem?.metadata?.membership?.planId,
  ])

  const isCashPaymentType = useMemo<boolean>(() => {
    if (product) return true

    if (!state.pendingPurchaseItem?.items) return false

    return state.pendingPurchaseItem?.items?.some(
      item => item.item.paymentType === UserPaymentType.Cash
    )
  }, [state.pendingPurchaseItem, product])

  const isShowUsageTicketNote = useMemo<boolean>(
    () =>
      !!state.pendingPurchaseItem?.items?.some(
        item => item.item.type === ItemType.Ticket
      ),
    [state.pendingPurchaseItem?.items]
  )

  const pathButtonComplete = useMemo(() => {
    if (product) {
      return generatePath(routerPages.MembershipManagement, {
        artistId: state.pendingPurchaseItem?.artistId,
      })
    }
    return state.pendingPurchaseItem?.cartTransactionOrderId
      ? generatePath(routerPages.StorePurchaseOrderDetail, {
          purchaseOrderId: state.pendingPurchaseItem?.cartTransactionOrderId,
        })
      : routerPages.StorePurchaseOrders
  }, [generatePath, product, routerPages, state.pendingPurchaseItem])

  return (
    <BaseLayout
      header={{
        back: true,
        defaultBackPage: routerPages.PaymentStatuses,
        title: t('title'),
      }}
      customNavigation={renderBottomItem()}
    >
      <Head title={routerNames.PaymentStatusDetail} />
      <Box sx={{ px: 5 }}>
        <Box sx={{ pt: 3, pb: 6 }}>
          <OrderDetailCell
            loading={state.loading}
            formattedTotalPrice={
              product?.plan?.formattedAmount ??
              state.pendingPurchaseItem?.formattedTotalPrice ??
              '0'
            }
            orderItems={state.orderItems}
            status={state.pendingPurchaseItem?.status}
            formattedExpiredAt={
              state.pendingPurchaseItem?.formattedExpiredAt ?? ''
            }
            product={product}
            shippingFee={state.shippingFee}
            systemFee={state.systemFee}
            konbiniFee={state.konbiniFee}
            totalPrice={state.pendingPurchaseItem?.totalPrice}
            purchaseType={isCashPaymentType ? 'konbini' : 'prepaid_point'}
            paymentType={
              isCashPaymentType
                ? UserPaymentType.Cash
                : UserPaymentType.PrepaidPoint
            }
          />
        </Box>
        <PurchaseInfo loading={state.loading} artist={state.artist} />
        {personalInformationRequirements.includes(
          PersonalInformationType.Address
        ) && (
          <ShippingAddress
            loading={state.loading}
            fullName={state.fullName}
            address={state.address}
            isPhoneNumberVerified={state.isPhoneNumberVerified}
            phoneNumber={state.phoneNumber}
            personalInformationRequirements={personalInformationRequirements}
          />
        )}
        <PaymentInfo
          loading={state.loading}
          formattedPrepaidPointBalance={state.formattedPrepaidPointBarance}
          purchaseType="konbini"
          showNote
        />

        {isShowUsageTicketNote && (
          <Typography component="p" variant="caption" color="text.secondary">
            {t('usageTicketNote')}
          </Typography>
        )}

        <Stack sx={{ mt: 6, px: 5, pb: 8 }}>
          {/* 購入済みであれば、購入履歴までの導線を表示 */}
          {state.pendingPurchaseItem?.status ===
            PendingPurchaseItemStatus.Completed && (
            <Button variant="outlined" size="large" href={pathButtonComplete}>
              {!product ? t('confirmOrderDetail') : t('viewMembership')}
            </Button>
          )}
        </Stack>
      </Box>
    </BaseLayout>
  )
}

type OrderDetailCellProps = TotalPaymentCellProps & {
  loading: State['loading']
  product?: MembershipSubscriptionProduct
  orderItems: State['orderItems']
  formattedTotalPrice: string
  status?: PendingPurchaseItemStatus
  formattedExpiredAt: string
}
const OrderDetailCell: Cell<OrderDetailCellProps> = ({
  loading,
  product,
  orderItems,
  formattedTotalPrice,
  status,
  formattedExpiredAt,
  ...props
}) => {
  const { t } = useTranslation('paymentStatusDetail')
  if (loading) {
    return <OrderItemDetailSkeleton />
  }
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Typography variant="h5" color="text.secondary" sx={{ py: 3 }}>
        {t('orderDetail', '注文内容')}
      </Typography>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 4 } }}>
          <Stack direction="row" spacing={2} sx={{ pb: 2 }} alignItems="center">
            {status && <SettlementTag status={status} />}
            <Typography
              variant="subtitle2"
              color={
                status === PendingPurchaseItemStatus.WaitingPayment
                  ? 'primary.main'
                  : 'text.disabled'
              }
            >
              {t('paymentDeadline')}：{formattedExpiredAt}
            </Typography>
          </Stack>
          {product ? (
            <Stack spacing={1}>
              <Typography variant="subtitle1">{product.title}</Typography>
              <Typography variant="subtitle1">
                {t(`${product.plan.interval}Fee`, {
                  amount: product.plan.formattedAmount,
                })}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={5}>
              {orderItems?.map((orderItem, index) => (
                <OrderItemCell
                  key={index}
                  item={orderItem.item}
                  quantity={orderItem.count}
                />
              ))}
            </Stack>
          )}
          <Divider sx={{ my: 3 }} />
          <TotalPaymentCell
            {...props}
            formattedTotalPrice={formattedTotalPrice}
          />
        </CardContent>
      </Card>
    </CellFrame>
  )
}

type FixedBottomCellProps = {
  expiredAt: string
  voucherUrl: string
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  expiredAt,
  voucherUrl,
}) => {
  const { t } = useTranslation('paymentStatusDetail')
  const { onClick } = useBrowserLink(voucherUrl)
  return (
    <CellFrame sx={{ bgcolor: 'grey.50' }}>
      <Stack sx={{ p: 5 }} alignItems="center" spacing={3}>
        <Typography variant="subtitle1">
          {t('paymentDeadline', { expiredAt })}
        </Typography>
        <Button
          variant="text"
          color="primary"
          sx={{ width: 160 }}
          disabled={!voucherUrl}
          onClick={onClick}
        >
          {t('confirmPayment')}
        </Button>
      </Stack>
    </CellFrame>
  )
}
